import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import SellerService from "../../services/seller.service";
import { CotizacionEntradas, PaymentMethods } from "../../types/common.types";
import formatCLP from "../../utils/formatCLP";

// Paso "Entradas y pago" de las tres pantallas de venta del vendedor (pasajero suelto, grupo y "+" de un
// tour), 16/10/2026. Antes cada una pedía UN monto de entrada para todo el grupo (un adulto y un niño de
// 6 pagaban lo mismo) y los pagos se escribían a mano. Ahora:
// - La entrada de cada pasajero la calcula el backend con las tarifas de la pestaña Parques (edad a la
//   fecha del tour y nacionalidad). El vendedor solo elige "Incluidas" o "Pagan en el parque"; el monto
//   lo puede cambiar solo Molantur, desde el admin.
// - El pago se elige como 100% / 80% / 20% del total de cada pasajero (tour + su entrada) con un medio
//   de pago, u "Otro" para escribir montos (lo que pagó el grupo en total, repartido en proporción a lo
//   que debe cada uno).

export const MEDIOS_DE_PAGO = ['Efectivo', 'Tarjeta Santander', 'Tarjeta BCI', 'Trasnferencia Santander', 'Transferencia BCI', 'Wise David', 'Link de Pago', 'Deuda']

export type Porcentaje = 100 | 80 | 20 | 'otro'

export interface EstadoPago {
    ticket_included: boolean
    porcentaje?: Porcentaje
    medio?: string
    otros: PaymentMethods[]
}

interface Opciones {
    tourId?: string
    fecha?: Date | string
    pasajeros: { id: string, name?: string }[]
    // Lo que paga de tour cada pasajero (en un privado, su parte del vehículo).
    preciosTour: number[]
}

// Reparte un monto entero en proporción a los pesos (el resto, de a $1, a los primeros).
export function repartirProporcional(monto: number, pesos: number[]): number[] {
    const total = pesos.reduce((s, p) => s + p, 0)
    if (!pesos.length) return []
    if (total <= 0) {
        const base = Math.floor(monto / pesos.length)
        return pesos.map((_, i) => base + (i < monto - base * pesos.length ? 1 : 0))
    }
    const partes = pesos.map(p => Math.floor(monto * p / total))
    let resto = Math.round(monto) - partes.reduce((s, p) => s + p, 0)
    for (let i = 0; resto > 0; i = (i + 1) % partes.length, resto--) partes[i] += 1
    return partes
}

// Suma los pagos de todos los pasajeros por medio de pago (lo que se manda en un privado, que el
// backend reparte entre los del vehículo).
export function sumarPorMedio(pagos: PaymentMethods[][]): PaymentMethods[] {
    const total: Record<string, number> = {}
    for (const ps of pagos) for (const m of ps) total[m.name] = (total[m.name] || 0) + Number(m.value || 0)
    return Object.entries(total).map(([name, value]) => ({ name, value } as PaymentMethods))
}

export default function useEntradasYPago({ tourId, fecha, pasajeros, preciosTour }: Opciones) {
    const [estado, setEstado] = useState<EstadoPago>({ ticket_included: true, otros: [] })
    const [cotizacion, setCotizacion] = useState<CotizacionEntradas>()
    const [cargando, setCargando] = useState(false)
    const ids = pasajeros.map(p => p.id).filter(Boolean).join(',')
    const fechaClave = fecha instanceof Date ? fecha.getTime() : fecha

    useEffect(() => {
        let vigente = true
        setCotizacion(undefined)
        if (!tourId || !fecha || !ids) return
        setCargando(true)
        SellerService.cotizarEntradas(tourId, fecha, ids.split(','))
            .then(res => { if (vigente) setCotizacion(res.data) })
            .catch(() => { if (vigente) setCotizacion(undefined) })
            .finally(() => { if (vigente) setCargando(false) })
        return () => { vigente = false }
    }, [tourId, fechaClave, ids]) // eslint-disable-line react-hooks/exhaustive-deps

    const sinParques = !!cotizacion?.sin_parques
    const incluidas = estado.ticket_included && !sinParques
    const entradas = pasajeros.map(p => incluidas ? (cotizacion?.pasajeros.find(c => c.passenger_id === p.id)?.monto || 0) : 0)
    const totales = pasajeros.map((_, i) => (Number(preciosTour[i]) || 0) + entradas[i])
    const totalGrupo = totales.reduce((s, t) => s + t, 0)
    // Quien el parque no autoriza bloquea siempre; los demás problemas del cálculo solo con entradas incluidas.
    const noAutorizados = (cotizacion?.pasajeros || []).filter(p => p.prohibido).map(p => p.error as string)
    const bloqueos = noAutorizados.length ? noAutorizados : incluidas ? (cotizacion?.errores || []) : []

    // Pagos de cada pasajero (en el mismo orden). Vacío si no se registró pago.
    const pagos: PaymentMethods[][] = useMemo(() => {
        if (estado.porcentaje && estado.porcentaje !== 'otro') {
            if (!estado.medio) return pasajeros.map(() => [])
            const pct = estado.porcentaje
            return totales.map(t => [{ name: estado.medio as string, value: Math.round(t * pct / 100) }])
        }
        if (estado.porcentaje === 'otro') {
            const porMedio = estado.otros.filter(m => Number(m.value) > 0).map(m => ({ name: m.name, partes: repartirProporcional(Number(m.value), totales) }))
            return pasajeros.map((_, i) => porMedio.map(m => ({ name: m.name, value: m.partes[i] })).filter(m => m.value > 0))
        }
        return pasajeros.map(() => [])
    }, [estado, totales.join(','), pasajeros.length]) // eslint-disable-line react-hooks/exhaustive-deps

    const pagado = pagos.reduce((s, ps) => s + ps.reduce((a, m) => a + Number(m.value || 0), 0), 0)

    // Lo que falta antes de poder vender, o null.
    const falta = bloqueos[0]
        || (estado.porcentaje && estado.porcentaje !== 'otro' && !estado.medio ? 'Elige el medio de pago.' : null)
        || (estado.porcentaje === 'otro' && estado.otros.some(m => !Number(m.value)) ? 'Escribe el monto de cada medio de pago.' : null)

    // ticket_included que va a cada reserva (sin parques: el backend lo deja vacío igual).
    const ticketIncluded = sinParques ? null : estado.ticket_included

    return { estado, setEstado, cotizacion, cargando, sinParques, incluidas, entradas, totales, totalGrupo, bloqueos, pagos, pagado, falta, ticketIncluded, pasajeros }
}

export type EntradasYPagoCtrl = ReturnType<typeof useEntradasYPago>

// sinPago (17/10/2026): en la venta en grupo el pago se registra al final, para toda la venta (PagoVenta).
export function EntradasYPago({ ctrl, sinPago }: { ctrl: EntradasYPagoCtrl, sinPago?: boolean }) {
    const { estado, setEstado, sinParques, incluidas, entradas, totales, totalGrupo, bloqueos, pagado, cargando, pasajeros, cotizacion } = ctrl
    const set = (cambio: Partial<EstadoPago>) => setEstado(prev => ({ ...prev, ...cambio }))
    const totalEntradas = entradas.reduce((s, e) => s + e, 0)

    return (
        <Main>
            <h3>Entradas a parques</h3>
            {sinParques ? (
                <p className="nota">Este tour no tiene entradas a parques.</p>
            ) : (
                <div className="opciones" role="group" aria-label="Entradas a parques">
                    <button type="button" aria-pressed={estado.ticket_included} className={estado.ticket_included ? 'activo' : ''}
                        onClick={() => set({ ticket_included: true })}>Entradas incluidas</button>
                    <button type="button" aria-pressed={!estado.ticket_included} className={!estado.ticket_included ? 'activo' : ''}
                        onClick={() => set({ ticket_included: false })}>Pagan en el parque</button>
                </div>
            )}
            {bloqueos.length > 0 && <p className="alerta" role="alert">{bloqueos.join(' ')}</p>}
            {incluidas && !bloqueos.length && cotizacion && (
                <ul className="detalle">
                    {pasajeros.map((p, i) => {
                        const c = cotizacion.pasajeros.find(x => x.passenger_id === p.id)
                        return <li key={p.id}><span>{p.name || 'Pasajero'}{c?.edad != null ? ` · ${c.edad} años` : ''}</span><strong>{entradas[i] ? formatCLP(entradas[i]) : 'Gratis'}</strong></li>
                    })}
                </ul>
            )}

            <div className="totales">
                <div><span>Tour</span><strong>{formatCLP(totalGrupo - totalEntradas)}</strong></div>
                {incluidas && <div><span>Entradas</span><strong>{cargando ? '…' : formatCLP(totalEntradas)}</strong></div>}
                <div className="total"><span>Total</span><strong>{formatCLP(totalGrupo)}</strong></div>
            </div>

            {!sinPago && <>
            <h3>Pago</h3>
            <div className="opciones" role="group" aria-label="Cuánto pagó">
                {([100, 80, 20, 'otro'] as Porcentaje[]).map(p => (
                    <button type="button" key={String(p)} aria-pressed={estado.porcentaje === p} className={estado.porcentaje === p ? 'activo' : ''}
                        onClick={() => set({ porcentaje: estado.porcentaje === p ? undefined : p })}>{p === 'otro' ? 'Otro' : `${p}%`}</button>
                ))}
            </div>
            {estado.porcentaje && estado.porcentaje !== 'otro' && (
                <label className="medio">
                    Medio de pago
                    <select value={estado.medio || ''} onChange={e => set({ medio: e.target.value || undefined })} aria-label="Medio de pago">
                        <option value="">—</option>
                        {MEDIOS_DE_PAGO.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </label>
            )}
            {estado.porcentaje === 'otro' && (
                <div className="otros">
                    {estado.otros.map((m, i) => (
                        <div className="fila" key={m.name}>
                            <span>{m.name}</span>
                            <input type="number" min={0} aria-label={`Monto ${m.name}`} placeholder="Monto" value={m.value || ''}
                                onChange={e => set({ otros: estado.otros.map((x, j) => j === i ? { ...x, value: Number(e.target.value) } : x) })} />
                            <button type="button" className="quitar" aria-label={`Quitar ${m.name}`}
                                onClick={() => set({ otros: estado.otros.filter((_, j) => j !== i) })}>✕</button>
                        </div>
                    ))}
                    <select value="" aria-label="Agregar medio de pago" onChange={e => e.target.value && set({ otros: [...estado.otros, { name: e.target.value } as PaymentMethods] })}>
                        <option value="">+ Agregar medio de pago</option>
                        {MEDIOS_DE_PAGO.filter(m => !estado.otros.some(o => o.name === m)).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    {pasajeros.length > 1 && <p className="nota">Lo que pagó el grupo en total: se reparte en proporción a lo que debe cada uno.</p>}
                </div>
            )}
            {estado.porcentaje && (
                <p className="nota">Pagado {formatCLP(pagado)} de {formatCLP(totalGrupo)} · saldo {formatCLP(Math.max(totalGrupo - pagado, 0))}</p>
            )}
            {totales.length > 1 && estado.porcentaje && estado.porcentaje !== 'otro' && (
                <p className="nota">{estado.porcentaje}% del total de cada pasajero.</p>
            )}
            </>}
        </Main>
    )
}

const Main = styled.div`
    margin-top: 16px;
    h3 { margin: 16px 0 8px; font-size: 15px; }
    .opciones { display: flex; flex-wrap: wrap; gap: 6px; }
    .opciones button {
        border: 1px solid #c9c9c9;
        border-radius: 999px;
        padding: 6px 14px;
        background: #fff;
        cursor: pointer;
        font-size: 13px;
    }
    .opciones button.activo { background: #2c3e50; border-color: #2c3e50; color: #fff; }
    .nota { margin: 8px 0 0; font-size: 12px; color: #777; }
    .alerta { margin: 8px 0 0; font-size: 13px; color: #b45309; }
    .detalle { list-style: none; padding: 0; margin: 10px 0 0; font-size: 13px; }
    .detalle li { display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px solid #f0f0f0; }
    .totales { margin-top: 12px; font-size: 14px; }
    .totales div { display: flex; justify-content: space-between; padding: 2px 0; }
    .totales .total { border-top: 1px solid #ddd; margin-top: 4px; padding-top: 6px; }
    .medio { display: flex; flex-direction: column; gap: 4px; margin-top: 10px; font-size: 13px; }
    select, input { border: 1px solid #c9c9c9; border-radius: 8px; padding: 7px; font-size: 13px; }
    .otros { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
    .otros .fila { display: grid; grid-template-columns: 1fr 1fr 28px; gap: 6px; align-items: center; font-size: 13px; }
    .otros .quitar { border: none; background: none; color: #c0392b; cursor: pointer; }
`
